import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer/Footer';
import Menu from '../../../components/Menu/Menu';
import Pagination from '../../../components/Pagination/Pagination';
import Toast from '../../../components/Toast/Toast';
import {
    deleteBacktest,
    getBacktests,
} from '../../../services/BacktestsService';
import { confirmDelete } from '../../../utils/confirmDialog';
import BacktestRow from './BacktestRow';

// while a backtest is running the list polls for status changes
const POLLING_MS = 5000;

function Backtests() {
    const navigate = useNavigate();

    const defaultLocation = useLocation();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    const [backtests, setBacktests] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(getPage());
    const [notification, setNotification] = useState({ type: '', text: '' });

    function loadBacktests(currentPage) {
        return getBacktests(currentPage || 1)
            .then(result => {
                setBacktests(result.rows);
                setCount(result.count);
                return result.rows;
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    useEffect(() => {
        loadBacktests(page);
    }, [page]);

    const [polling, setPolling] = useState(false);

    useEffect(() => {
        if (!polling) return;

        const timer = setInterval(
            () =>
                loadBacktests(page).then(rows => setPolling(hasRunning(rows))),
            POLLING_MS,
        );
        return () => clearInterval(timer);
    }, [polling, page]);

    function hasRunning(rows) {
        return rows ? rows.some(r => r.status === 'running') : false;
    }

    useEffect(() => {
        setPolling(hasRunning(backtests));
    }, [backtests]);

    function onViewClick(event) {
        navigate(
            '/automations/backtests/' + event.target.id.replace('view', ''),
        );
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        confirmDelete('This backtest will be permanently deleted!').then(
            confirmed => {
                if (!confirmed) return;
                deleteBacktest(id)
                    .then(() => loadBacktests(page))
                    .catch(err => {
                        console.error(
                            err.response ? err.response.data : err.message,
                        );
                        setNotification({
                            type: 'error',
                            text: err.response
                                ? err.response.data
                                : err.message,
                        });
                    });
            },
        );
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">Backtests</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <button
                                id="btnNewBacktest"
                                className="btn btn-primary"
                                onClick={() =>
                                    navigate('/automations/backtests/new')
                                }
                            >
                                <svg
                                    className="icon icon-xs me-2"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                New Backtest
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">Date</th>
                                <th className="border-gray-200">Symbol</th>
                                <th className="border-gray-200">Status</th>
                                <th className="border-gray-200">Base %</th>
                                <th className="border-gray-200">Quote %</th>
                                <th className="border-gray-200">Total %</th>
                                <th className="border-gray-200">Operations</th>
                                <th className="border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backtests && backtests.length ? (
                                backtests.map(backtest => (
                                    <BacktestRow
                                        key={backtest.id}
                                        data={backtest}
                                        onViewClick={onViewClick}
                                        onDeleteClick={onDeleteClick}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="text-center py-3"
                                    >
                                        No backtests yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                <Footer />
            </main>
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default Backtests;
